/**
 * Section subtotals for the inbox block card (2026-08-05).
 *
 * The requirement that shaped this file: a subtotal must agree TO THE CENT with
 * the rows printed above it. Two things make that non-obvious, and both are why
 * the sums live here rather than inline in the card.
 *
 * 1. `lineCents` is the ONE definition of what a line is worth, imported by the
 *    row that prints it (InboxLaborTable / InboxEquipmentTable /
 *    InboxMaterialsList) and by the sum below. Rounding happens PER ROW and the
 *    rounded values are then added — `sum(round(h × r))`, never
 *    `round(sum(h × r))`. The two differ by up to half a cent per row, and the
 *    first is the only one that matches what the reader can add up by hand.
 *
 * 2. The labor and equipment sums read the card's LIVE edit state, not the
 *    server row, because that is what the table renders — `edits[id]` with a
 *    `?? 0` fallback on both halves. A row with no edit entry prints $0.00 and
 *    must contribute $0.00. Mirroring the fallback here is deliberate, not
 *    defensive.
 *
 * Nothing here bills. The server recomputes every total from the saved payload;
 * these are read-only sums of what is already on screen.
 */

/** Value of one line in cents. Units are hours (labor/equipment) or quantity
 *  (materials); the rounding boundary is the line, never the section. */
export function lineCents(units: number, rateCents: number): number {
  return Math.round(units * rateCents);
}

interface EditedHours {
  total_hours: number;
  rate_cents: number;
}

interface EditedEquipmentHours {
  hours: number;
  rate_cents: number;
}

export function laborSubtotalCents(
  rows: readonly { daily_labor_entry_id: string }[],
  edits: Readonly<Record<string, EditedHours | undefined>>,
): number {
  return rows.reduce((sum, row) => {
    const edit = edits[row.daily_labor_entry_id];
    return sum + lineCents(edit?.total_hours ?? 0, edit?.rate_cents ?? 0);
  }, 0);
}

export function equipmentSubtotalCents(
  rows: readonly { daily_equipment_entry_id: string }[],
  edits: Readonly<Record<string, EditedEquipmentHours | undefined>>,
): number {
  return rows.reduce((sum, row) => {
    const edit = edits[row.daily_equipment_entry_id];
    return sum + lineCents(edit?.hours ?? 0, edit?.rate_cents ?? 0);
  }, 0);
}

/** Materials price off stored values, not edit state — the inbox has no inline
 *  material editor, so confirmed wins and suggested is the fallback, exactly as
 *  the list renders each row. */
export function materialsSubtotalCents(
  rows: readonly {
    confirmed_quantity: number | null;
    suggested_quantity: number | null;
    confirmed_rate_cents: number | null;
    suggested_default_rate_cents: number;
  }[],
): number {
  return rows.reduce(
    (sum, row) =>
      sum +
      lineCents(
        row.confirmed_quantity ?? row.suggested_quantity ?? 0,
        row.confirmed_rate_cents ?? row.suggested_default_rate_cents ?? 0,
      ),
    0,
  );
}
